<script setup lang="ts">
import { computed } from "vue";
import { AwSkeleton } from "@asap/design";
import { formatPercent, MISSING_KPI_VALUE } from "./theme";
import type { KpiTile } from "./types";

const SKELETON_TILE_COUNT = 3;

const props = withDefaults(
  defineProps<{
    title?: string;
    ratio?: { value: string; label: string; detail: string };
    occupancy?: { label: string; percent: number | null };
    kpis?: KpiTile[];
    loading?: boolean;
  }>(),
  { title: "Économie du planning", ratio: undefined, occupancy: undefined, kpis: undefined, loading: false }
);

const headlineMetrics = computed(() => [
  { key: "ratio", value: props.ratio?.value, label: props.ratio?.label, detail: props.ratio?.detail },
  {
    key: "occupancy",
    value:
      props.occupancy?.percent === null || props.occupancy?.percent === undefined
        ? MISSING_KPI_VALUE
        : formatPercent(props.occupancy.percent),
    label: props.occupancy?.label,
  },
]);

const ui = {
  headline: "grid grid-cols-2 gap-3 rounded-xl bg-secondary-50 p-3",
  metric: "flex items-center gap-3",
  metricValueBox:
    "flex h-14 min-w-14 shrink-0 items-center justify-center rounded-lg bg-secondary-100 px-3 text-3xl font-semibold leading-none text-secondary-900",
  metricDetails: "flex min-w-0 flex-col",
  metricLabel: "text-sm text-secondary-900",
  metricDetail: "text-xs text-secondary-700",
  kpiGrid: "grid grid-cols-3 gap-3",
  skeletonValueBox: "size-14 shrink-0 rounded-lg",
  skeletonLabel: "h-3.5 w-24",
  skeletonDetail: "mt-1 h-3 w-32",
};
</script>

<template>
  <ConsultantKpiCard :title="title">
    <div :class="ui.headline">
      <div v-for="metric in headlineMetrics" :key="metric.key" :class="ui.metric">
        <template v-if="loading">
          <AwSkeleton :class="ui.skeletonValueBox" />
          <div :class="ui.metricDetails">
            <AwSkeleton :class="ui.skeletonLabel" />
            <AwSkeleton :class="ui.skeletonDetail" />
          </div>
        </template>
        <template v-else>
          <span :class="ui.metricValueBox">{{ metric.value }}</span>
          <div :class="ui.metricDetails">
            <span :class="ui.metricLabel">{{ metric.label }}</span>
            <span v-if="metric.detail" :class="ui.metricDetail">{{ metric.detail }}</span>
          </div>
        </template>
      </div>
    </div>

    <div :class="ui.kpiGrid">
      <template v-if="loading">
        <ConsultantKpiTile
          v-for="index in SKELETON_TILE_COUNT"
          :key="`planning-skeleton-${index}`"
          loading
          variant="plain"
        />
      </template>
      <template v-else>
        <ConsultantKpiTile v-for="kpi in kpis" :key="kpi.label" v-bind="kpi" />
      </template>
    </div>
  </ConsultantKpiCard>
</template>
